'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { ToolOfflineMode } from '@/lib/tools/tool-registry';

function getOfflineCopy(offlineMode: ToolOfflineMode, isOnline: boolean) {
  if (isOnline) {
    return null;
  }

  if (offlineMode === 'online-required') {
    return {
      tone: 'warning' as const,
      title: 'Connect first',
      message: 'This tool needs an internet connection before it can run.',
    };
  }

  if (offlineMode === 'limited') {
    return {
      tone: 'info' as const,
      title: 'Limited while offline',
      message: 'Some live features may pause until you reconnect, but local editing can still continue.',
    };
  }

  return {
    tone: 'success' as const,
    title: 'Offline-ready',
    message: 'You are offline. This tool can still work on this device, and sync can continue when you reconnect.',
  };
}

const toneStyles = {
  info: 'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100',
  warning:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100',
  success:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-100',
} as const;

export default function OfflineToolNotice({
  offlineMode,
  isOnline,
  compact = true,
}: {
  offlineMode: ToolOfflineMode;
  isOnline: boolean;
  compact?: boolean;
}) {
  const copy = getOfflineCopy(offlineMode, isOnline);

  if (!copy) {
    return null;
  }

  return (
    <section data-component-id="src/components/OfflineToolNotice.tsx:59:4" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="59" data-component-end-line="69" data-component-file="OfflineToolNotice.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%7D" className={`${compact ? 'rounded-full px-3 py-2' : 'rounded-2xl px-4 py-4'} border ${toneStyles[copy.tone]}`}>
      <div data-component-id="src/components/OfflineToolNotice.tsx:60:6" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="60" data-component-end-line="68" data-component-file="OfflineToolNotice.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex ${compact ? 'items-center gap-2.5' : 'items-start gap-3'}`}>
        <div data-component-id="src/components/OfflineToolNotice.tsx:61:8" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="61" data-component-end-line="63" data-component-file="OfflineToolNotice.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${compact ? 'rounded-full p-1.5' : 'mt-0.5 rounded-xl p-2'} bg-white/70 dark:bg-slate-950/40`}>
          <Icon data-component-id="src/components/OfflineToolNotice.tsx:62:10" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="62" data-component-end-line="62" data-component-file="OfflineToolNotice.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={copy.tone === 'success' ? 'CheckBadgeIcon' : 'WifiIcon'} size={16} />
        </div>
        <div data-component-id="src/components/OfflineToolNotice.tsx:64:8" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="64" data-component-end-line="67" data-component-file="OfflineToolNotice.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
          <p data-component-id="src/components/OfflineToolNotice.tsx:65:10" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="65" data-component-end-line="65" data-component-file="OfflineToolNotice.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" className={`${compact ? 'text-xs font-bold' : 'text-sm font-bold'}`}>{copy.title}</p>
          <p data-component-id="src/components/OfflineToolNotice.tsx:66:10" data-component-path="src/components/OfflineToolNotice.tsx" data-component-line="66" data-component-end-line="66" data-component-file="OfflineToolNotice.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" className={`${compact ? 'text-xs opacity-80' : 'mt-1 text-sm leading-6 opacity-90'}`}>{copy.message}</p>
        </div>
      </div>
    </section>
  );
}
